import { useEffect, useRef } from "react";
import { IconSave } from "@/components/icons";

interface UnsavedChangesDialogProps {
  isSaving: boolean;
  onSave: () => void;
  onDiscard: () => void;
  onCancel: () => void;
}

export function UnsavedChangesDialog({
  isSaving,
  onSave,
  onDiscard,
  onCancel,
}: UnsavedChangesDialogProps) {
  const saveButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    saveButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !isSaving) onCancel();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isSaving, onCancel]);

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center px-4 py-8"
      role="dialog"
      aria-modal="true"
      aria-labelledby="unsaved-form-dialog-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-slate-950/40 backdrop-blur-[2px]"
        aria-label="Продолжить редактирование"
        disabled={isSaving}
        onClick={onCancel}
      />
      <div className="relative w-full max-w-sm rounded-lg border border-slate-200 bg-white px-5 py-5 text-center shadow-2xl">
        <h2
          id="unsaved-form-dialog-title"
          className="font-onest text-lg font-semibold text-slate-950"
        >
          Сохранить форму?
        </h2>
        <p className="mt-1.5 font-onest text-sm leading-relaxed text-slate-500">
          В форме остались несохранённые изменения.
        </p>
        <div className="mt-5 flex flex-col items-stretch">
          <button
            ref={saveButtonRef}
            type="button"
            disabled={isSaving}
            onClick={onSave}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 font-onest text-sm font-medium text-white shadow-md shadow-red-600/20 transition-colors hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500/40 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <IconSave size={16} />
            {isSaving ? "Сохранение…" : "Сохранить"}
          </button>
          <button
            type="button"
            disabled={isSaving}
            onClick={onDiscard}
            className="mt-3 self-center font-onest text-sm text-slate-500 transition-colors hover:text-slate-800 focus-visible:outline-none focus-visible:underline disabled:cursor-not-allowed disabled:opacity-50"
          >
            Выйти без сохранения
          </button>
        </div>
      </div>
    </div>
  );
}
